<script setup lang="ts">
import { useField, useForm } from 'vee-validate'
import { computed, watch } from 'vue'
import * as yup from 'yup'
import { getAllUsersAction } from '@/api/functions/transactions/get-transaction-details.action'
import { useQueryApi } from '@/composables/useApi'

interface RequisitionFormValues {
  requestType: 'BUY' | 'SELL' | ''
  requester: string
  amount: number | ''
  currency: string
  exchange: number | ''
  destinationAccount: string
  sourceCountry: string
  transferMethod: string[]
  validityPeriod: number | ''
  discount: string
  description: string
}

const props = defineProps<{
  values: RequisitionFormValues
  countries: Array<{ label: string; value: string | number }>
  currencies: Array<{ label: string; value: string | number }>
  discountOptions: Array<{ label: string; value: string | number }>
  transferMethods: Array<{
    label: string
    value: string | number
  }>
}>()

const emit = defineEmits<(e: 'submit', values: RequisitionFormValues) => void>()

interface User {
  id: number
  uuid: string
  fullName: string
}

// Users
const { data: usersResponse } = useQueryApi({
  queryKey: ['users'],
  queryFn: () => getAllUsersAction(),
  staleTime: 1000 * 60 * 5,
})

const usersSelectOptions = computed(() => {
  const users = usersResponse.value?.data ?? []

  // TODO : Replace static mobile & email with dynamic API values.
  return users.map((user: User) => ({
    label: `\u2068${user.id}\u2069 | \u2068${user.fullName}\u2069 | \u206809121234567+\u2069 | \u2068user@example.com\u2069`,
    value: user.uuid,
  }))
})

// Validation
const schema = yup.object({
  requestType: yup.string().required('نوع درخواست الزامی است'),

  requester: yup.string().required('درخواست کننده الزامی است'),

  amount: yup
    .number()
    .typeError('مقدار ارز باید عدد باشد')
    .required('مقدار ارز الزامی است')
    .positive('مقدار ارز باید بیشتر از صفر باشد'),

  exchange: yup
    .number()
    .typeError('نرخ تبادل باید عدد باشد')
    .required('نرخ تبادل الزامی است')
    .positive('نرخ تبادل باید بیشتر از صفر باشد'),

  currency: yup.string().required('واحد ارز الزامی است'),

  destinationAccount: yup.string().required('حساب مقصد الزامی است'),

  sourceCountry: yup.string().when('requestType', {
    is: 'BUY',
    then: (schema) => schema.required('کشور مبدا الزامی است'),
    otherwise: (schema) => schema.notRequired(),
  }),

  transferMethod: yup
    .array()
    .of(yup.string())
    .when('requestType', {
      is: 'SELL',
      then: (schema) => schema.min(1, 'حداقل یک روش انتقال را انتخاب کنید').required('روش انتقال الزامی است'),
      otherwise: (schema) => schema.notRequired(),
    }),

  validityPeriod: yup
    .number()
    .typeError('مدت اعتبار باید عدد باشد')
    .required('مدت اعتبار الزامی است')
    .positive('مدت اعتبار باید بیشتر از صفر باشد'),

  discount: yup.string().required('تخفیف الزامی است'),

  description: yup.string().optional(),
})

// Form
const { handleSubmit, errors } = useForm<RequisitionFormValues>({
  validationSchema: schema,

  initialValues: {
    requestType: props.values.requestType,
    requester: props.values.requester,
    amount: props.values.amount,
    exchange: props.values.exchange,
    currency: props.values.currency,
    destinationAccount: props.values.destinationAccount,
    sourceCountry: props.values.sourceCountry,
    transferMethod: [...props.values.transferMethod],
    validityPeriod: props.values.validityPeriod,
    discount: props.values.discount,
    description: props.values.description,
  },
})

// Fields
const { value: requestType } = useField<'BUY' | 'SELL' | ''>('requestType')

const { value: requester } = useField<string>('requester')

const { value: amount } = useField<number | ''>('amount')

const { value: currency } = useField<string>('currency')

const { value: exchange } = useField<number | ''>('exchange')

const { value: destinationAccount } = useField<string>('destinationAccount')

const { value: sourceCountry } = useField<string>('sourceCountry')

const { value: transferMethod } = useField<string[]>('transferMethod')

const { value: validityPeriod } = useField<number | ''>('validityPeriod')

const { value: discount } = useField<string>('discount')

const { value: description } = useField<string>('description')

// Submit
const onSubmit = handleSubmit((values) => {
  emit('submit', values)
})

// Set first currency when no currency has been selected yet
watch(
  () => props.currencies,
  (currencies) => {
    if (!currency.value && currencies?.length) {
      currency.value = String(currencies[0].value)
    }
  },
  {
    immediate: true,
  }
)
</script>

<template>
    <form id="create-requisition-form" class="px-8 pt-2 pb-10" @submit.prevent="onSubmit">
        <div class="flex flex-col lg:grid grid-cols-2 gap-2 lg:gap-y-2 lg:gap-x-3">
            <!-- Request Type - Full Row -->
            <div class="col-span-2 mb-6">
                <Radio v-model="requestType" name="requestType" appearance="admin" :options="[
                    {
                        label: 'فروش',
                        value: 'SELL',
                    },
                    {
                        label: 'خرید',
                        value: 'BUY',
                    },
                ]" />

                <span v-if="errors.requestType" class="block text-xs text-red-500 mt-1">
                    {{ errors.requestType }}
                </span>
            </div>

            <!-- Requester -->
            <div class="col-span-2">
                <Select v-model="requester" name="requester" label="درخواست کننده" :options="usersSelectOptions"
                    direction="ltr" :error="errors.requester" appearance="admin" :searchable="true" />
            </div>

            <!-- Amount + Currency -->
            <InputWithSelect v-model="amount" v-model:selectValue="currency" name="amount" label="مقدار ارز"
                type="number" appearance="admin" :options="currencies" :error="errors.amount" />

            <!-- Exchange -->
            <InputWithCounter v-model="exchange" name="exchange" label="نرخ تبادل" type="number" suffix="تومان"
                appearance="admin" class="mt-6 lg:mt-0" />

            <!-- Destination Account -->
            <IranianBankAccountSelect v-if="requestType === 'SELL'" label="حساب مقصد" v-model="destinationAccount"
                :error="errors.destinationAccount" :countries="countries" :accounts="[
                    {
                        id: '1',
                        bankName: 'بانک ملی ایران',
                        cardNumber: '6037997512345678',
                        shebaNumber: 'IR120170000000123456789012',
                        ownerFullName: 'علی احمدی',
                    },
                    {
                        id: '2',
                        bankName: 'بانک ملت',
                        cardNumber: '6104331234567890',
                        shebaNumber: 'IR450120000000987654321098',
                        ownerFullName: 'محمد رضایی',
                    },
                    {
                        id: '3',
                        bankName: 'بانک سامان',
                        cardNumber: '6219861234567890',
                        shebaNumber: 'IR680560000000456789123456',
                        ownerFullName: 'سارا محمدی',
                    },
                    {
                        id: '4',
                        bankName: 'بانک پاسارگاد',
                        cardNumber: '5022291234567890',
                        shebaNumber: 'IR210570000000789456123789',
                        ownerFullName: 'رضا کریمی',
                    },
                ]" />

            <InternationalBankAccountSelect v-if="requestType === 'BUY'" v-model="destinationAccount" label="حساب مقصد"
                :error="errors.destinationAccount" :transfer-methods="transferMethods" :countries="countries"
                :international-accounts="[
                    {
                        id: 'international-1',
                        bankName: 'Deutsche Bank',
                        accountNumber: '1234567890',
                        iban: 'DE89370400440532013000',
                        ownerFullName: 'John Doe',
                    },
                    {
                        id: 'international-2',
                        bankName: 'Commerzbank',
                        accountNumber: '9876543210',
                        iban: 'DE44500105175407324931',
                        ownerFullName: 'John Doe',
                    },
                    {
                        id: 'international-3',
                        bankName: 'BNP Paribas',
                        accountNumber: '4567891230',
                        iban: 'FR7630006000011234567890189',
                        ownerFullName: 'John Doe',
                    },
                    {
                        id: 'international-4',
                        bankName: 'ING Bank',
                        accountNumber: '7412589630',
                        iban: 'NL91ABNA0417164300',
                        ownerFullName: 'John Doe',
                    },
                ]" :paypal-accounts="[
                    {
                        id: 'paypal-1',
                        bankName: 'PayPal',
                        accountNumber: 'john.doe@example.com',
                        ownerFullName: 'John Doe',
                    },
                ]" :revolut-accounts="[
                    {
                        id: 'revolut-1',
                        bankName: 'Revolut',
                        accountNumber: 'GB12REVO1234567890',
                        ownerFullName: 'John Doe',
                    },
                ]" />

            <!-- Source Country -->
            <CountrySelect v-if="requestType === 'SELL'" v-model="sourceCountry" name="sourceCountry"
                :error="errors.sourceCountry" appearance="admin" label="کشور مبدا" class="mt-6 lg:mt-0" />

            <!-- Transfer Method -->
            <MultiSelect v-if="requestType === 'SELL'" v-model="transferMethod" name="transferMethod" label="روش انتقال"
                :options="transferMethods" :error="errors.transferMethod" appearance="admin" variant="checkbox" />

            <!-- Validity Period -->
            <InputWithCounter v-model="validityPeriod" name="validityPeriod" label="مدت اعتبار" type="number"
                suffix="روز" appearance="admin" :class="requestType === 'BUY' ? 'mt-6 lg:mt-0' : ''" />

            <!-- Discount - Full Row -->
            <div class="col-span-2">
                <Select v-model="discount" name="discount" label="تخفیف" :options="discountOptions"
                    :error="errors.discount" appearance="admin" />
            </div>

            <!-- Description - Full Row -->
            <div class="col-span-2">
                <Input v-model="description" name="description" label="توضیحات (اختیاری)" appearance="admin" />
            </div>
        </div>

        <div
            class="flex flex-col gap-3.5 bg-[#F6F6F6] border border-[#E0E0E0] text-[#1A1A1A] rounded-xl p-4 mt-2 lg:mt-0">
            <div class="flex items-center justify-between font-bold">
                <span class="text-xs">
                    مجموع {{ requestType === 'SELL' ? 'دریافتی' : 'پرداختی' }} کاربر
                </span>

                <span class="font-fa text-lg">
                    {{ (210009).toLocaleString() }} تومان
                </span>
            </div>

            <div class="flex items-center justify-between">
                <span class="text-xs">خالص</span>

                <span class="font-fa text-sm">
                    {{ (2130000).toLocaleString() }} تومان
                </span>
            </div>

            <div class="flex items-center justify-between">
                <div class="flex flex-col gap-2 text-xs">
                    <span>
                        کارمزد
                        <span class="text-[#0E59FA]">نحوه محاسبه؟</span>
                    </span>

                    <span>حاوی 10% مالیات بر ارزش افزوده</span>
                </div>

                <span class="font-fa text-sm">
                    {{ (12300).toLocaleString() }} تومان
                </span>
            </div>
        </div>
    </form>
</template>

<style scoped></style>
